import React from 'react';
import {css} from 'emotion';
import Text from '@amzn/meridian/text';
import {useAppSelector} from '../../../app/hooks';
import {selectTheme} from '../../themes/themeStateSlice';
import {ITheme} from '../../../common/styles/themes/models';
import {SummaryRecord, ConstraintValueRecord, DateIdMap} from '../utils/analyzeConstraints';

interface IProps {
  summaryRecords: SummaryRecord[];
  minimumConstraints: DateIdMap;
  constraintsAffectingOperationsData: ConstraintValueRecord[];
}

const sectionStyle = css`
  margin-bottom: 16px;
  margin-top: 12px;
`;

const getWrapperStyle = (theme: ITheme) => css`
  border-radius: 3px;
  box-shadow: 0px 2px 4px ${theme.TableShadow};
  overflow-x: auto;
  max-height: 75.5vh;
`;

const getTableCss = (theme: ITheme) => css`
  width: 100%;
  min-width: 1500px;
  border-collapse: collapse;
  font-size: 13px;

  thead tr th {
    padding: 8px 10px;
    font-size: 14px;
    font-weight: bold;
    color: ${theme.RowHeader};
    background-color: ${theme.RowHeaderBackground};
    border-bottom: 1px solid ${theme.RowHeaderBorder};
    border-right: 1px solid ${theme.RowHeaderBorder};
    white-space: nowrap;
  }

  tbody tr td {
    padding: 8px 10px;
    color: ${theme.TextPrimary};
    border-bottom: 1px solid ${theme.RowBorder};
    border-right: 1px solid ${theme.RowBorder};
    background-color: ${theme.RowPrimary};
    white-space: nowrap;
  }

  tbody tr:nth-of-type(even) td {
    background-color: ${theme.RowAlternate};
    border-bottom: 1px solid ${theme.RowAlternateBorder};
    border-right: 1px solid ${theme.RowAlternateBorder};
  }

  tbody tr:hover td {
    background-color: ${theme.RowHighlight};
    border-bottom: 1px solid ${theme.RowHighlightBorder};
    border-right: 1px solid ${theme.RowHighlightBorder};
  }
`;

const labelStyle = css`
  color: #555;
  padding-bottom: 4px;
`;

const SiteSummarySection = ({summaryRecords, minimumConstraints, constraintsAffectingOperationsData}: IProps) => {
  const theme = useAppSelector(selectTheme);

  if (!summaryRecords || summaryRecords.length === 0) {
    return null;
  }

  // Get date columns from the first summary record
  const excludeKeys = new Set(['summaryTitle', 'summaryTitleTooltip', 'summaryDetailRows']);
  const firstRecord = summaryRecords[0];
  const dateColumns = Object.keys(firstRecord).filter((k) => !excludeKeys.has(k) && firstRecord[k] !== undefined);

  // Build constraint name lookup
  const constraintNameMap: Record<string, string> = {};
  constraintsAffectingOperationsData.forEach((c) => {
    constraintNameMap[c.constraintId] = c.constraintName;
  });

  // Build constraint reason row from minimumConstraints
  const constraintReasonRow: Record<string, {name: string; id: string}> = {};
  dateColumns.forEach((date) => {
    const constraintId = minimumConstraints[date];
    if (constraintId) {
      constraintReasonRow[date] = {
        name: constraintNameMap[constraintId] || constraintId,
        id: constraintId,
      };
    }
  });

  const wrapperStyle = getWrapperStyle(theme);
  const tableCss = getTableCss(theme);

  return (
    <div className={sectionStyle}>
      <Text type="h100" className={labelStyle}>
        Weekly Summary
      </Text>
      <div className={wrapperStyle}>
        <table className={tableCss}>
          <thead>
            <tr>
              <th style={{textAlign: 'left', position: 'sticky', left: 0, zIndex: 10, minWidth: 180}}></th>
              {dateColumns.map((date) => {
                const d = new Date(date + 'T00:00:00');
                return (
                  <th key={date} style={{textAlign: 'right', minWidth: 85}}>
                    {d.toLocaleString('default', {month: 'short'})}-{d.toLocaleString('default', {day: '2-digit'})}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {summaryRecords.map((record) => {
              const isGapRow = String(record.summaryTitle).includes('Gap');
              return (
                <tr key={String(record.summaryTitle)}>
                  <td
                    style={{
                      textAlign: 'left',
                      fontWeight: 'bold',
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      backgroundColor: theme.StickyColumnBackground,
                      color: theme.StickyColumn,
                    }}
                  >
                    {String(record.summaryTitle)}
                  </td>
                  {dateColumns.map((date) => {
                    const val = String(record[date] || '');
                    const isNegative = val.startsWith('-');
                    const cellStyle = isGapRow
                      ? {
                          color: isNegative ? theme.ErrorInfoText : theme.SuccessInfoText,
                          backgroundColor: isNegative ? theme.ErrorInfoBackground : theme.SuccessInfoBackground,
                        }
                      : {};
                    return (
                      <td key={date} style={{textAlign: 'right', ...cellStyle}}>
                        {val}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            <tr>
              <td
                style={{
                  textAlign: 'left',
                  fontWeight: 'bold',
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                  backgroundColor: theme.StickyColumnBackground,
                  color: theme.StickyColumn,
                }}
              >
                Constraint Reason
              </td>
              {dateColumns.map((date) => {
                const reason = constraintReasonRow[date];
                if (!reason) {
                  return (
                    <td key={date} style={{textAlign: 'right'}}>
                      —
                    </td>
                  );
                }
                return (
                  <td key={date} style={{textAlign: 'right', fontSize: 11, fontWeight: 'bold'}}>
                    {reason.name}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SiteSummarySection;
